import { type FC, useCallback, useRef, useEffect, useState, memo } from 'react';
import type { AnimationStepProps } from '../../types';
import { Overlay, Header, OverlayAlignment } from '../Overlay';
import { CustomButton } from '../Overlay/styles';
import { useAbyssViewerContext } from '../AbyssViewerContext';

interface AnimationEditorProps {
  initialAnimationSteps?: AnimationStepProps[];
}

const fontSizeProperty: React.CSSProperties = { fontSize: 12 };

export const AnimationEditor: FC<AnimationEditorProps> = memo(({ initialAnimationSteps = [] }) => {
  const { callbacks } = useAbyssViewerContext();

  const animationSteps = useRef<AnimationStepProps[]>(initialAnimationSteps);

  const [numberSteps, setNumberSteps] = useState<number>(initialAnimationSteps.length);

  useEffect(() => {
    if (initialAnimationSteps && initialAnimationSteps.length > 0) {
      animationSteps.current = initialAnimationSteps;
      setNumberSteps(initialAnimationSteps.length);
    }
  }, [initialAnimationSteps]);

  const addCameraMoveStep = useCallback(() => {
    const cameraTarget = callbacks?.getCameraPosition?.();

    if (cameraTarget) {
      const step: AnimationStepProps = {
        type: 'moveCamera',
        name: `step ${animationSteps.current.length}`,
        cameraPosition: cameraTarget?.position,
        cameraLookAt: cameraTarget?.lookAt,
        time: 1,
      };
      animationSteps.current.push(step);
      setNumberSteps(animationSteps.current.length);
    }
  }, [callbacks]);

  const addPauseStep = useCallback(() => {
    const cameraTarget = callbacks?.getCameraPosition?.();

    if (cameraTarget) {
      const step: AnimationStepProps = {
        type: 'pause',
        name: `step ${animationSteps.current.length}`,
        time: 5,
      };
      animationSteps.current.push(step);
      setNumberSteps(animationSteps.current.length);
    }
  }, [callbacks]);

  const removeLastStep = useCallback(() => {
    if (animationSteps.current.length > 0) {
      animationSteps.current.pop();
      setNumberSteps(animationSteps.current.length);
    }
  }, []);

  const playAnimation = useCallback(() => {
    const animation = {
      name: 'my animation',
      steps: animationSteps.current,
    };
    if (callbacks?.startAnimation)
      callbacks.startAnimation(animation, () => {
        alert("animation's done!");
      });
  }, [callbacks]);

  const exportAnimationJson = useCallback(() => {
    const animation = {
      name: 'my animation',
      steps: animationSteps.current,
    };
    const animationJson = JSON.stringify(animation, null, 2);
    navigator.clipboard.writeText(animationJson);
  }, []);

  const importAnimationJson = useCallback(async () => {
    const animationJsonString = await navigator.clipboard.readText();
    const animationJson = JSON.parse(animationJsonString);

    animationSteps.current = animationJson.steps;
    setNumberSteps(animationJson.steps.length);
  }, []);

  return (
    <>
      <Overlay alignment={OverlayAlignment.right}>
        <Header text="Animation Editor" />
        <CustomButton variant="contained" onClick={addCameraMoveStep}>
          Add Camera Move
        </CustomButton>
        <CustomButton variant="contained" onClick={addPauseStep}>
          Add Pause
        </CustomButton>
        <CustomButton variant="contained" onClick={removeLastStep}>
          Remove Last Step
        </CustomButton>
        <CustomButton variant="contained" onClick={playAnimation}>
          Play animation
        </CustomButton>
        <CustomButton variant="contained" onClick={exportAnimationJson}>
          Copy Json
        </CustomButton>
        <CustomButton variant="contained" onClick={importAnimationJson}>
          Paste Json
        </CustomButton>
        <div style={fontSizeProperty}>{`${numberSteps} steps`}</div>
      </Overlay>
    </>
  );
});
AnimationEditor.displayName = 'AnimationEditor';
